import React, { useState, useEffect, useCallback } from 'react';
import { getPosters, getPosterCategories, getImageUrl, API_BASE_URL } from '../model/api.js';
import LoadingSpinner from './components/common/LoadingSpinner.jsx';
import { GeneralInsuranceTab } from './components/GeneralInsuranceTab.jsx';
import { MutualFundTab } from './components/MutualFundTab.jsx';
import ProfileTab from './components/ProfileTab.jsx';
import HealthInsuranceTab from './components/HealthInsuranceTab.jsx';
import { toast } from 'react-toastify';

const HealthPlusDashboard = ({ user, refreshUser }) => {
    if (!user || !user.id) {
        return <LoadingSpinner />;
    }

    const [activeTab, setActiveTab] = useState('health-insurance');
    const [posters, setPosters] = useState([]);
    const [posterCategories, setPosterCategories] = useState([]);
    const [posterLoading, setPosterLoading] = useState(false);
    const [selectedPosterCategory, setSelectedPosterCategory] = useState('all');
    const [posterViewMode, setPosterViewMode] = useState('grid');
    const [downloadingPosterId, setDownloadingPosterId] = useState(null);


    const fetchPosters = useCallback(async () => {
        setPosterLoading(true);
        try {
            const params = {
                category: selectedPosterCategory === 'all' ? undefined : selectedPosterCategory,
                limit: 50
            };
            const result = await getPosters(params);
            if (result.ok && result.data) {
                setPosters(result.data.posters || []);
            } else {
                setPosters([]);
            }
        } catch (error) {
            console.error('Error fetching posters:', error);
            toast.error('Failed to fetch posters');
        } finally {
            setPosterLoading(false);
        }
    }, [selectedPosterCategory]);

    const fetchPosterCategories = useCallback(async () => {
        try {
            const result = await getPosterCategories();
            if (result.ok && result.data) {
                setPosterCategories(result.data);
            }
        } catch (error) {
            console.error('Error fetching poster categories:', error);
        }
    }, []);

    const isNewPoster = (createdAt) => {
        if (!createdAt) return false;
        const now = new Date();
        const createdDate = new Date(createdAt);
        const diffInHours = (now - createdDate) / (1000 * 60 * 60);
        return diffInHours <= 168; // 7 days (7 * 24 = 168 hours)
    };

    useEffect(() => {
        if (activeTab === 'posters') {
            fetchPosters();
            fetchPosterCategories();
        }
    }, [activeTab, fetchPosters, fetchPosterCategories]);

    const downloadPersonalizedPoster = async (posterId, posterName, userName) => {
        setDownloadingPosterId(posterId);
        try {
            const getCookie = (name) => {
                const value = `; ${document.cookie}`;
                const parts = value.split(`; ${name}=`);
                if (parts.length === 2) return parts.pop().split(';').shift();
                return null;
            };

            const token = getCookie('token');
            const headers = {};
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE_URL}/posters/${posterId}/download-personalized`, {
                headers
            });

            if (!response.ok) {
                // Safely parse error - handle both JSON and non-JSON responses
                let errorMessage = `Failed to download poster (${response.status})`;
                try {
                    const contentType = response.headers.get('content-type') || '';
                    if (contentType.includes('json')) {
                        const errorData = await response.json();
                        errorMessage = errorData.error || errorData.message || errorMessage;
                    } else {
                        const text = await response.text();
                        errorMessage = text || errorMessage;
                    }
                } catch (parseErr) {
                    console.warn('Could not parse error response:', parseErr);
                }
                throw new Error(errorMessage);
            }

            const blob = await response.blob();
            if (blob.size === 0) throw new Error('Downloaded file is empty');

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${posterName}_${userName}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast.success('Poster downloaded successfully!');
        } catch (error) {
            console.error('Download error:', error);
            toast.error(`Download failed: ${error.message}`);
        } finally {
            setDownloadingPosterId(null);
        }
    };

    const renderContent = () => {
        switch (activeTab) {
            case 'health-insurance':
                return <HealthInsuranceTab userRole="health_plus_partner" />;
            case 'general-insurance':
                return <GeneralInsuranceTab userRole="health_plus_partner" />;
            case 'mutual-funds':
                return <MutualFundTab userRole="health_plus_partner" />;
            case 'posters':
                return (
                    <div className="space-y-6">
                        <div className="flex justify-end">
                            <select
                                value={selectedPosterCategory}
                                onChange={(e) => setSelectedPosterCategory(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500"
                            >
                                <option value="all">All Categories</option>
                                {posterCategories.map(cat => <option key={cat._id} value={cat._id}>{cat.name}</option>)}
                            </select>
                        </div>
                        {posterLoading ? <LoadingSpinner /> : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                {posters.map(poster => (
                                    <div key={poster._id} className="bg-white rounded-lg shadow-md overflow-hidden">
                                        <div className="aspect-w-3 aspect-h-4 bg-gray-200 relative">
                                            <img
                                                src={`${API_BASE_URL}/posters/${poster._id}/image`}
                                                alt={poster.name}
                                                className="w-full h-48 object-cover cursor-pointer hover:opacity-80"
                                                onClick={() => window.open(`${API_BASE_URL}/posters/${poster._id}/image`, '_blank')}
                                            />
                                            {isNewPoster(poster.createdAt) && (
                                                <span className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded shadow-sm z-10">
                                                    NEW
                                                </span>
                                            )}
                                            <button
                                                onClick={(e) => { e.stopPropagation(); downloadPersonalizedPoster(poster._id, poster.name, user.name); }}
                                                className="absolute top-2 right-2 bg-indigo-600 text-white p-2 rounded-full hover:bg-indigo-700 shadow-lg"
                                            >
                                                {downloadingPosterId === poster._id ? (
                                                    <div className="animate-spin h-4 w-4 border-2 border-white rounded-full border-t-transparent"></div>
                                                ) : (
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path></svg>
                                                )}
                                            </button>
                                        </div>
                                        <div className="p-4">
                                            <h3 className="font-semibold text-gray-900 mb-1">{poster.name}</h3>
                                            <p className="text-sm text-gray-600 truncate">{poster.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            case 'profile':
                return <ProfileTab user={user} onUpdate={refreshUser} />;
            default:
                return null;
        }
    };

    return (
        <div className="space-y-8 max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900">Welcome, {user.name}</h2>
            <nav className="bg-white p-2 rounded-lg shadow-md flex space-x-2 overflow-x-auto">
                {['health-insurance', 'general-insurance', 'mutual-funds', 'posters', 'profile'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${activeTab === tab ? 'bg-indigo-600 text-white shadow' : 'text-gray-600 hover:bg-gray-100'
                            }`}
                    >
                        {tab.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </button>
                ))}
            </nav>
            <div className="mt-6">{renderContent()}</div>
        </div>
    );
};

export default HealthPlusDashboard;
